import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Link,
    List,
    ListItemButton,
    ListItemText
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import i18n from '../../../../i18n';
import MobileBottomSheet from '../../../../components/mobile/MobileBottomSheet';
import {
    applyButtonSx,
    resetLinkSx,
    segmentSx,
    segmentsRowSx,
    sortRowSx
} from './GridMobileFilterSortSheet.styles';

// A phone's filters and sorting, in one sheet with a tab each. Sorting gets
// its own list because a phone has no column heads to tap; a row steps
// through the same three states a head does. Filters apply as they change,
// so Apply only closes the sheet, saying how many rows there now are.
export default function GridMobileFilterSortSheet({
    open,
    onClose,
    tab,
    onTabChange,
    filterContent,
    sortFields,
    sortConfig,
    onSortField,
    onResetFilters,
    totalCount
}) {
    const isFilter = tab === 'filter';
    const hasCount = typeof totalCount === 'number' && totalCount >= 0;

    const renderSortRow = (field) => {
        const active = sortConfig?.field === field.id;
        const direction = active ? sortConfig.direction : null;

        return (
            <ListItemButton
                key={field.id}
                onClick={() => onSortField(field.id)}
                sx={sortRowSx(active)}
            >
                <ListItemText
                    primary={i18n.t(`columns.${field.label}`)}
                    primaryTypographyProps={{
                        fontSize: 15,
                        fontWeight: active ? 800 : 600,
                        color: 'inherit'
                    }}
                />
                {direction === 'asc' && <ArrowUpwardIcon fontSize="small" />}
                {direction === 'desc' && <ArrowDownwardIcon fontSize="small" />}
            </ListItemButton>
        );
    };

    const segment = (value, label) => (
        <Box
            component="button"
            type="button"
            aria-pressed={tab === value}
            sx={segmentSx(tab === value)}
            onClick={() => onTabChange(value)}
        >
            {label}
        </Box>
    );

    return (
        <MobileBottomSheet
            open={open}
            onClose={onClose}
            title={isFilter ? i18n.t('mobile.refine') : i18n.t('mobile.sortBy')}
            headerRight={
                isFilter && onResetFilters ? (
                    <Link
                        component="button"
                        type="button"
                        underline="none"
                        sx={resetLinkSx}
                        onClick={onResetFilters}
                    >
                        {i18n.t('mobile.reset')}
                    </Link>
                ) : null
            }
            footer={
                <Button
                    fullWidth
                    variant="contained"
                    sx={applyButtonSx}
                    onClick={onClose}
                >
                    {isFilter && hasCount
                        ? i18n.t('mobile.applyResults', { count: totalCount })
                        : i18n.t('mobile.apply')}
                </Button>
            }
        >
            <Box sx={segmentsRowSx}>
                {segment('filter', i18n.t('mobile.filter'))}
                {segment('sort', i18n.t('mobile.sort'))}
            </Box>

            {isFilter ? (
                filterContent
            ) : (
                <List disablePadding>{sortFields.map(renderSortRow)}</List>
            )}
        </MobileBottomSheet>
    );
}

GridMobileFilterSortSheet.propTypes = {
    filterContent: PropTypes.node,
    onClose: PropTypes.func.isRequired,
    onResetFilters: PropTypes.func,
    onSortField: PropTypes.func.isRequired,
    onTabChange: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    sortConfig: PropTypes.shape({
        field: PropTypes.string,
        direction: PropTypes.string
    }),
    sortFields: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired
        })
    ).isRequired,
    tab: PropTypes.oneOf(['filter', 'sort']).isRequired,
    totalCount: PropTypes.number
};
